import React, { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { 
  GraduationCap, BookOpen, Shield, Wallet, TrendingUp, Layers, 
  Clock, Users, Award, Play, ChevronRight, Filter, ChevronLeft,
  Coins, Globe, Zap, Target, CheckCircle, Star, ArrowRight,
  X, ArrowLeft, ArrowRightIcon, Check, AlertTriangle, Lightbulb, Info
} from "lucide-react";

// ============================================
// COMPREHENSIVE LESSON CONTENT DATA
// ============================================

const LESSON_CONTENT = {
  "crypto-basics": [
    {
      title: "What is Cryptocurrency?",
      duration: "8 min",
      image: "https://images.unsplash.com/photo-1635840420248-1ba20fd1aac2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHxjcnlwdG9jdXJyZW5jeSUyMGJsb2NrY2hhaW4lMjBlZHVjYXRpb24lMjBsZWFybmluZ3xlbnwwfHx8fDE3NzE4NDU2Mjd8MA&ixlib=rb-4.1.0&q=85",
      content: [
        {
          type: "text",
          value: "Cryptocurrency is a revolutionary form of digital money that exists purely in electronic form. Unlike the dollars in your wallet or the balance in your bank account, cryptocurrencies aren't controlled by any government or central bank."
        },
        {
          type: "highlight",
          title: "Key Definition",
          value: "Cryptocurrency is a digital or virtual currency secured by cryptography, making it nearly impossible to counterfeit or double-spend. Most cryptocurrencies are decentralized networks based on blockchain technology."
        },
        {
          type: "text",
          value: "Think of cryptocurrency as digital cash that you can send to anyone, anywhere in the world, without needing a bank or payment processor as an intermediary. The transaction happens directly between you and the recipient — this is called a peer-to-peer (P2P) transaction."
        },
        {
          type: "image",
          url: "https://images.pexels.com/photos/14348481/pexels-photo-14348481.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
          caption: "Bitcoin whitepaper - the document that started it all"
        },
        {
          type: "subheading",
          value: "How Does It Work?"
        },
        {
          type: "text",
          value: "Cryptocurrencies use advanced mathematics and computer science (cryptography) to secure transactions and control the creation of new units. When you send cryptocurrency, the transaction is broadcast to a network of computers (called nodes) that verify and record it on a shared ledger called a blockchain."
        },
        {
          type: "list",
          title: "Key Properties of Cryptocurrency",
          items: [
            "Decentralized: No single entity controls the network",
            "Transparent: All transactions are recorded on a public ledger",
            "Secure: Cryptography makes it extremely difficult to hack or forge",
            "Borderless: Send money anywhere in the world instantly",
            "Permissionless: Anyone can participate without approval"
          ]
        },
        {
          type: "example",
          title: "Real-World Example",
          value: "Imagine you want to send $100 to a friend in Japan. With traditional banking, this could take 3-5 days and cost $25-50 in fees. With Bitcoin, the same transaction takes about 10 minutes and costs a few dollars, regardless of the amount being sent."
        }
      ]
    },
    {
      title: "History of Bitcoin",
      duration: "10 min",
      image: "https://images.pexels.com/photos/14348491/pexels-photo-14348491.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      content: [
        {
          type: "text",
          value: "The story of Bitcoin begins in 2008, during one of the worst financial crises in modern history. Banks were failing, governments were bailing them out with taxpayer money, and trust in the traditional financial system was at an all-time low."
        },
        {
          type: "highlight",
          title: "The Genesis",
          value: "On October 31, 2008, a person (or group) using the pseudonym 'Satoshi Nakamoto' published a nine-page whitepaper titled 'Bitcoin: A Peer-to-Peer Electronic Cash System.' This document laid out the blueprint for the first cryptocurrency."
        },
        {
          type: "image",
          url: "https://images.unsplash.com/photo-1644925024902-8f5829da8204?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxiaXRjb2luJTIwaGlzdG9yeSUyMHNhdG9zaGklMjBuYWthbW90byUyMGNyeXB0b2N1cnJlbmN5JTIwb3JpZ2lufGVufDB8fHx8MTc3MTg0NTk2OXww&ixlib=rb-4.1.0&q=85",
          caption: "Bitcoin - the first and most valuable cryptocurrency"
        },
        {
          type: "subheading",
          value: "Key Milestones in Bitcoin History"
        },
        {
          type: "timeline",
          items: [
            { year: "2008", event: "Satoshi Nakamoto publishes the Bitcoin whitepaper" },
            { year: "2009", event: "The Bitcoin network goes live; first block (Genesis Block) mined on January 3" },
            { year: "2010", event: "First real-world Bitcoin transaction: 10,000 BTC for two pizzas (worth ~$700M today!)" },
            { year: "2011", event: "Bitcoin reaches $1 for the first time; other cryptocurrencies begin to emerge" },
            { year: "2013", event: "Bitcoin crosses $1,000; mainstream media begins coverage" },
            { year: "2017", event: "Bitcoin reaches nearly $20,000; crypto enters mainstream consciousness" },
            { year: "2021", event: "Bitcoin hits all-time high of $69,000; El Salvador adopts it as legal tender" },
            { year: "2024", event: "Bitcoin ETFs approved in the US; institutional adoption accelerates" }
          ]
        },
        {
          type: "text",
          value: "The identity of Satoshi Nakamoto remains one of the greatest mysteries in technology. Despite numerous investigations and claims, no one has definitively proven who created Bitcoin. Satoshi disappeared from public communication in 2011, leaving behind an estimated 1 million bitcoins that have never been moved."
        },
        {
          type: "warning",
          value: "The early Bitcoin pizza transaction is a famous reminder of Bitcoin's growth. Those 10,000 BTC spent on two pizzas in 2010 would be worth over $900 million at Bitcoin's peak price!"
        }
      ]
    },
    {
      title: "Types of Cryptocurrencies",
      duration: "8 min",
      image: "https://images.unsplash.com/photo-1707075891517-c23d276feef7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHw0fHxiaXRjb2luJTIwaGlzdG9yeSUyMHNhdG9zaGklMjBuYWthbW90byUyMGNyeXB0b2N1cnJlbmN5JTIwb3JpZ2lufGVufDB8fHx8MTc3MTg0NTk2OXww&ixlib=rb-4.1.0&q=85",
      content: [
        {
          type: "text",
          value: "While Bitcoin was the first cryptocurrency, thousands of others have been created since. Each serves different purposes and has unique characteristics. Understanding the main categories will help you navigate the crypto ecosystem."
        },
        {
          type: "subheading",
          value: "1. Bitcoin (BTC) - Digital Gold"
        },
        {
          type: "text",
          value: "Bitcoin remains the largest and most well-known cryptocurrency. Often called 'digital gold,' it's primarily used as a store of value and investment. With a maximum supply of 21 million coins, Bitcoin is designed to be scarce and deflationary."
        },
        {
          type: "highlight",
          title: "Bitcoin Dominance",
          value: "As of 2024, Bitcoin represents approximately 50% of the total cryptocurrency market capitalization, making it by far the most dominant cryptocurrency."
        },
        {
          type: "subheading",
          value: "2. Ethereum (ETH) - The Smart Contract Platform"
        },
        {
          type: "text",
          value: "Ethereum expanded on Bitcoin's concept by adding programmable 'smart contracts' — self-executing agreements written in code. This enables decentralized applications (dApps), DeFi protocols, and NFTs. Think of Ethereum as a global computer that anyone can program."
        },
        {
          type: "subheading",
          value: "3. Stablecoins (USDT, USDC, DAI)"
        },
        {
          type: "text",
          value: "Stablecoins are cryptocurrencies designed to maintain a stable value, usually pegged 1:1 to the US dollar. They combine the benefits of cryptocurrency (fast, borderless transactions) with the stability of traditional currency."
        },
        {
          type: "list",
          title: "Popular Stablecoins",
          items: [
            "USDT (Tether): Largest stablecoin by market cap, backed by reserves",
            "USDC (USD Coin): Fully regulated, backed by cash and short-term treasuries",
            "DAI: Decentralized stablecoin, backed by crypto collateral"
          ]
        },
        {
          type: "subheading",
          value: "4. Altcoins - Alternative Cryptocurrencies"
        },
        {
          type: "text",
          value: "Altcoins (alternative coins) refer to any cryptocurrency other than Bitcoin. Some notable examples include:"
        },
        {
          type: "list",
          title: "Notable Altcoins",
          items: [
            "Solana (SOL): High-speed blockchain for DeFi and NFTs",
            "Cardano (ADA): Research-driven blockchain focused on security",
            "XRP: Designed for fast, cheap international payments",
            "Dogecoin (DOGE): Started as a meme, now a popular payment method",
            "Polkadot (DOT): Connects multiple blockchains together"
          ]
        }
      ]
    },
    {
      title: "How Crypto Transactions Work",
      duration: "7 min",
      image: "https://images.pexels.com/photos/8369659/pexels-photo-8369659.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      content: [
        {
          type: "text",
          value: "When you send cryptocurrency, you're not actually moving digital coins from one place to another. Instead, you're broadcasting a message to the network that updates who owns what on the shared ledger (blockchain)."
        },
        {
          type: "subheading",
          value: "Step-by-Step: How a Bitcoin Transaction Works"
        },
        {
          type: "steps",
          items: [
            {
              step: 1,
              title: "Create the Transaction",
              description: "You specify the recipient's address and the amount you want to send. Your wallet creates a transaction and signs it with your private key (like a digital signature)."
            },
            {
              step: 2,
              title: "Broadcast to the Network",
              description: "Your transaction is sent to the Bitcoin network, where it enters a waiting area called the 'mempool' (memory pool)."
            },
            {
              step: 3,
              title: "Verification by Miners",
              description: "Miners (specialized computers) collect pending transactions and verify they're valid — checking that you have enough balance and the signature is correct."
            },
            {
              step: 4,
              title: "Block Creation",
              description: "Valid transactions are grouped into a 'block.' Miners compete to solve a complex mathematical puzzle to add this block to the chain."
            },
            {
              step: 5,
              title: "Confirmation",
              description: "Once a block is added, your transaction is confirmed. Additional blocks added on top provide more security (6 confirmations is considered very secure)."
            }
          ]
        },
        {
          type: "highlight",
          title: "Transaction Fees",
          value: "Every transaction includes a fee paid to miners/validators. Higher fees = faster processing. During busy periods, fees can spike significantly. Some blockchains (like Solana) have fees of fractions of a cent, while Bitcoin fees can range from $1-$50+ depending on network congestion."
        },
        {
          type: "example",
          title: "Analogy",
          value: "Think of blockchain transactions like sending a certified letter. You write the letter (create transaction), mail it (broadcast), the post office verifies it's legitimate (miners), it gets stamped and logged (added to block), and finally delivered (confirmed). Everyone can verify the letter was sent and received."
        }
      ]
    },
    {
      title: "Crypto vs Traditional Money",
      duration: "6 min",
      image: "https://images.unsplash.com/photo-1768839720841-8219c4da7436?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHw0fHxkaWdpdGFsJTIwc2VjdXJpdHklMjBsb2NrJTIwY3liZXJzZWN1cml0eSUyMHByb3RlY3Rpb258ZW58MHx8fHwxNzcxODQ1NjQ2fDA&ixlib=rb-4.1.0&q=85",
      content: [
        {
          type: "text",
          value: "Cryptocurrency represents a fundamentally different approach to money compared to the traditional financial system. Understanding these differences is crucial for anyone entering the crypto space."
        },
        {
          type: "comparison",
          items: [
            {
              aspect: "Control",
              traditional: "Controlled by central banks and governments",
              crypto: "Decentralized, controlled by network participants"
            },
            {
              aspect: "Supply",
              traditional: "Can be printed infinitely (inflation)",
              crypto: "Often fixed supply (Bitcoin: 21 million max)"
            },
            {
              aspect: "Transactions",
              traditional: "Require intermediaries (banks, payment processors)",
              crypto: "Peer-to-peer, no middleman needed"
            },
            {
              aspect: "Speed",
              traditional: "International transfers: 1-5 business days",
              crypto: "Minutes to hours, 24/7/365"
            },
            {
              aspect: "Privacy",
              traditional: "Banks know all your transactions",
              crypto: "Pseudonymous (not fully anonymous)"
            },
            {
              aspect: "Access",
              traditional: "Requires bank account (1.4B unbanked globally)",
              crypto: "Anyone with internet access"
            }
          ]
        },
        {
          type: "highlight",
          title: "The Unbanked Opportunity",
          value: "Over 1.4 billion adults worldwide don't have access to traditional banking. Cryptocurrency offers them a way to store value, send money, and participate in the global economy with just a smartphone."
        },
        {
          type: "warning",
          value: "While crypto offers many advantages, it also comes with risks: price volatility, irreversible transactions, and the responsibility of securing your own funds. There's no 'forgot password' option or fraud protection like traditional banks provide."
        }
      ]
    },
    {
      title: "Getting Started with Crypto",
      duration: "6 min",
      image: "https://images.unsplash.com/photo-1758611974001-2e7ad7b83432?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxkaWdpdGFsJTIwd2FsbGV0JTIwbW9iaWxlJTIwcGhvbmUlMjBwYXltZW50fGVufDB8fHx8MTc3MTg0NTY1NXww&ixlib=rb-4.1.0&q=85",
      content: [
        {
          type: "text",
          value: "Ready to take your first steps into cryptocurrency? Here's a practical guide to getting started safely and responsibly."
        },
        {
          type: "steps",
          items: [
            {
              step: 1,
              title: "Educate Yourself First",
              description: "You're already doing this! Continue learning about blockchain, different cryptocurrencies, and security practices before investing any money."
            },
            {
              step: 2,
              title: "Choose a Reputable Exchange",
              description: "Start with established exchanges like Coinbase, Kraken, or Binance. Look for platforms with strong security, regulatory compliance, and good customer support. In Canada, look for FINTRAC-registered exchanges."
            },
            {
              step: 3,
              title: "Complete Verification (KYC)",
              description: "Legitimate exchanges require identity verification. This protects you and helps prevent money laundering. You'll typically need a government ID and proof of address."
            },
            {
              step: 4,
              title: "Start Small",
              description: "Only invest what you can afford to lose. A good starting point might be $50-$100 to learn how things work without significant risk."
            },
            {
              step: 5,
              title: "Make Your First Purchase",
              description: "Buy a small amount of Bitcoin or Ethereum to start. These are the most established and liquid cryptocurrencies."
            },
            {
              step: 6,
              title: "Learn About Wallets",
              description: "While exchange wallets are fine for small amounts, learn about self-custody options (hot and cold wallets) for larger holdings."
            }
          ]
        },
        {
          type: "highlight",
          title: "Golden Rule",
          value: "Never invest more than you can afford to lose completely. Cryptocurrency is volatile — prices can drop 50% or more in a matter of days. Only use money you won't need in the short term."
        },
        {
          type: "list",
          title: "Common Beginner Mistakes to Avoid",
          items: [
            "Investing based on FOMO (Fear of Missing Out)",
            "Not securing your accounts with 2FA",
            "Sharing your seed phrase or private keys",
            "Falling for 'get rich quick' schemes",
            "Trading with leverage before understanding it",
            "Not researching before buying altcoins"
          ]
        },
        {
          type: "tip",
          title: "Pro Tip",
          value: "Consider using Dollar-Cost Averaging (DCA) — investing a fixed amount regularly (e.g., $50/week) regardless of price. This reduces the impact of volatility and removes emotional decision-making."
        }
      ]
    }
  ],
  "blockchain-101": [
    {
      title: "What is Blockchain?",
      duration: "8 min",
      image: "https://images.pexels.com/photos/8369659/pexels-photo-8369659.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      content: [
        {
          type: "text",
          value: "Blockchain is the revolutionary technology that makes cryptocurrency possible. At its core, it's a special type of database — but one that's radically different from traditional databases in how it stores and shares information."
        },
        {
          type: "highlight",
          title: "Simple Definition",
          value: "A blockchain is a distributed digital ledger that records transactions across many computers in a way that makes it virtually impossible to alter past records. Think of it as a shared spreadsheet that everyone can see but no one can secretly change."
        },
        {
          type: "image",
          url: "https://images.pexels.com/photos/8369681/pexels-photo-8369681.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
          caption: "Blockchain concepts explained on a whiteboard"
        },
        {
          type: "subheading",
          value: "Why 'Block' + 'Chain'?"
        },
        {
          type: "text",
          value: "The name comes from how data is organized: transactions are grouped into 'blocks,' and these blocks are linked together in a 'chain' using cryptographic hashes. Each block contains a reference to the previous block, creating an unbreakable chain of history."
        },
        {
          type: "list",
          title: "Key Characteristics of Blockchain",
          items: [
            "Distributed: Copies exist on thousands of computers worldwide",
            "Immutable: Once recorded, data cannot be changed without detection",
            "Transparent: Anyone can view the entire transaction history",
            "Decentralized: No single point of failure or control",
            "Trustless: You don't need to trust any individual participant"
          ]
        },
        {
          type: "example",
          title: "Traditional Database vs Blockchain",
          value: "A traditional database is like a private notebook controlled by one person (a company or government). If they decide to change or delete something, no one would know. A blockchain is like a public noticeboard where everyone has a copy, and any changes require agreement from the majority."
        }
      ]
    },
    {
      title: "How Blocks Work",
      duration: "7 min",
      image: "https://images.unsplash.com/photo-1639762681057-408e52192e55?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHw0fHxldGhlcmV1bSUyMGRlZmklMjBzbWFydCUyMGNvbnRyYWN0cyUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzcxODQ1NjQxfDA&ixlib=rb-4.1.0&q=85",
      content: [
        {
          type: "text",
          value: "Each block in a blockchain is like a page in a ledger book. But unlike a regular page, each block is cryptographically sealed and linked to both the block before it and after it."
        },
        {
          type: "subheading",
          value: "Anatomy of a Block"
        },
        {
          type: "list",
          title: "Every block contains:",
          items: [
            "Block Header: Metadata including timestamp, version, and difficulty",
            "Previous Block Hash: A cryptographic link to the block before it",
            "Merkle Root: A summary of all transactions in the block",
            "Nonce: A number used in mining to find a valid hash",
            "Transaction Data: The actual transactions (who sent what to whom)"
          ]
        },
        {
          type: "highlight",
          title: "What is a Hash?",
          value: "A hash is like a digital fingerprint. It's a fixed-length string of characters generated from any input data. Even a tiny change to the input creates a completely different hash. Bitcoin uses SHA-256, which produces a 64-character hash."
        },
        {
          type: "example",
          title: "Hash Example",
          value: "Input: 'Hello' → Hash: 185f8db32271fe25f561a6fc938b2e26... \nInput: 'hello' → Hash: 2cf24dba5fb0a30e26e83b2ac5b9e29e... \nNotice how changing just one letter (H to h) creates a completely different hash!"
        },
        {
          type: "subheading",
          value: "Why Blocks Are Immutable"
        },
        {
          type: "text",
          value: "Because each block contains the hash of the previous block, changing any historical block would change its hash, which would break the link to the next block. You'd have to recalculate every subsequent block — and do it faster than the entire rest of the network. This is practically impossible."
        }
      ]
    },
    {
      title: "Decentralization Explained",
      duration: "8 min",
      image: "https://images.pexels.com/photos/18500862/pexels-photo-18500862.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      content: [
        {
          type: "text",
          value: "Decentralization is perhaps the most important concept in blockchain technology. It's what makes blockchain different from every other database or financial system that came before it."
        },
        {
          type: "highlight",
          title: "Definition",
          value: "Decentralization means distributing power and control away from a single central authority to a network of participants. No single entity can control, censor, or shut down a truly decentralized network."
        },
        {
          type: "subheading",
          value: "Centralized vs Decentralized"
        },
        {
          type: "comparison",
          items: [
            {
              aspect: "Control",
              traditional: "Single entity (bank, government, company)",
              crypto: "Distributed among thousands of participants"
            },
            {
              aspect: "Single Point of Failure",
              traditional: "Yes - if central server fails, everything stops",
              crypto: "No - network continues even if many nodes fail"
            },
            {
              aspect: "Censorship",
              traditional: "Can freeze accounts, block transactions",
              crypto: "Cannot be censored if sufficiently decentralized"
            },
            {
              aspect: "Trust",
              traditional: "Must trust the central authority",
              crypto: "Trust the code and mathematics"
            }
          ]
        },
        {
          type: "example",
          title: "Real-World Example",
          value: "In 2010, Visa, Mastercard, and PayPal blocked donations to WikiLeaks. But Bitcoin donations continued uninterrupted because there was no central authority that could block them. This demonstrated the power of decentralization for censorship resistance."
        },
        {
          type: "warning",
          value: "Not all cryptocurrencies are equally decentralized. Some have small numbers of validators or are controlled by a single company. True decentralization is a spectrum, and it's important to research how decentralized a project really is."
        }
      ]
    },
    {
      title: "Consensus Mechanisms",
      duration: "10 min",
      image: "https://images.unsplash.com/photo-1591489237701-57358ac6d20d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxiaXRjb2luJTIwbWluaW5nJTIwY29tcHV0ZXIlMjBoYXJkd2FyZSUyMGdwdXxlbnwwfHx8fDE3NzE4NDU5ODJ8MA&ixlib=rb-4.1.0&q=85",
      content: [
        {
          type: "text",
          value: "In a decentralized network, how do thousands of computers agree on what's true? This is the fundamental challenge that consensus mechanisms solve."
        },
        {
          type: "highlight",
          title: "The Problem",
          value: "In traditional systems, a central authority decides what's valid. In a decentralized network, participants must reach agreement (consensus) without trusting each other. This is known as the 'Byzantine Generals Problem' in computer science."
        },
        {
          type: "subheading",
          value: "Proof of Work (PoW)"
        },
        {
          type: "text",
          value: "Bitcoin uses Proof of Work, where miners compete to solve complex mathematical puzzles. The first to find a valid solution gets to add the next block and receive the reward. This process is intentionally difficult and energy-intensive to make cheating economically unfeasible."
        },
        {
          type: "image",
          url: "https://images.pexels.com/photos/4581816/pexels-photo-4581816.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
          caption: "GPUs used for cryptocurrency mining"
        },
        {
          type: "list",
          title: "Proof of Work Characteristics",
          items: [
            "High energy consumption (often criticized)",
            "Very secure and battle-tested",
            "Anyone with hardware can participate",
            "Rewards miners for computational work"
          ]
        },
        {
          type: "subheading",
          value: "Proof of Stake (PoS)"
        },
        {
          type: "text",
          value: "Proof of Stake selects validators based on how much cryptocurrency they 'stake' as collateral. If they validate fraudulent transactions, they lose their stake. This is much more energy-efficient than PoW."
        },
        {
          type: "list",
          title: "Proof of Stake Characteristics",
          items: [
            "99%+ more energy efficient than PoW",
            "Validators chosen by stake amount",
            "Bad behavior results in 'slashing' (losing staked funds)",
            "Used by Ethereum, Cardano, Solana, and many others"
          ]
        }
      ]
    },
    {
      title: "Public vs Private Blockchains",
      duration: "6 min",
      image: "https://images.unsplash.com/photo-1767424412548-1a1ac7f4b9bc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwzfHx0cmFkaW5nJTIwY2hhcnQlMjBjYW5kbGVzdGljayUyMHN0b2NrJTIwbWFya2V0JTIwYW5hbHlzaXN8ZW58MHx8fHwxNzcxODQ1OTg0fDA&ixlib=rb-4.1.0&q=85",
      content: [
        {
          type: "text",
          value: "Not all blockchains are created equal. Depending on who can participate and access data, blockchains fall into different categories."
        },
        {
          type: "subheading",
          value: "Public Blockchains"
        },
        {
          type: "text",
          value: "Public blockchains like Bitcoin and Ethereum are completely open. Anyone can join the network, view all transactions, and participate in validation. These are truly decentralized and censorship-resistant."
        },
        {
          type: "subheading",
          value: "Private Blockchains"
        },
        {
          type: "text",
          value: "Private blockchains are controlled by a single organization. Only approved participants can join, and the controlling entity can modify rules. These are essentially distributed databases with some blockchain properties."
        },
        {
          type: "subheading",
          value: "Consortium Blockchains"
        },
        {
          type: "text",
          value: "Consortium blockchains are semi-decentralized, controlled by a group of organizations rather than a single entity. They're often used in industries where multiple companies need to share data securely."
        },
        {
          type: "comparison",
          items: [
            {
              aspect: "Access",
              traditional: "Anyone can join and participate",
              crypto: "Restricted to approved participants"
            },
            {
              aspect: "Decentralization",
              traditional: "Fully decentralized",
              crypto: "Centralized or semi-centralized"
            },
            {
              aspect: "Use Case",
              traditional: "Cryptocurrencies, DeFi, public records",
              crypto: "Enterprise solutions, supply chain"
            }
          ]
        }
      ]
    },
    {
      title: "Smart Contracts Introduction",
      duration: "8 min",
      image: "https://images.unsplash.com/photo-1624956001740-f4ff24a5ef42?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxldGhlcmV1bSUyMGRlZmklMjBzbWFydCUyMGNvbnRyYWN0cyUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzcxODQ1NjQxfDA&ixlib=rb-4.1.0&q=85",
      content: [
        {
          type: "text",
          value: "Smart contracts are perhaps the most revolutionary application of blockchain technology beyond simple value transfer. They enable programmable, self-executing agreements."
        },
        {
          type: "highlight",
          title: "Definition",
          value: "A smart contract is a self-executing program stored on a blockchain that automatically enforces the terms of an agreement when predefined conditions are met. 'Code is law' — the contract executes exactly as programmed, without intermediaries."
        },
        {
          type: "example",
          title: "Simple Example",
          value: "Imagine a vending machine: you put in $2, select a snack, and the machine automatically dispenses it. No human needed. A smart contract works the same way but for digital agreements. If condition X is met, automatically do Y."
        },
        {
          type: "list",
          title: "Smart Contract Use Cases",
          items: [
            "DeFi (Decentralized Finance): Lending, borrowing, trading without banks",
            "NFTs: Proving ownership and enabling royalties for digital art",
            "Insurance: Automatic payouts when conditions are verified",
            "Supply Chain: Tracking products and releasing payments on delivery",
            "Voting: Transparent, tamper-proof elections"
          ]
        },
        {
          type: "warning",
          value: "Smart contracts are only as good as their code. Bugs can lead to massive losses — in 2016, a bug in 'The DAO' smart contract led to $60 million being stolen. Always use audited contracts and understand the risks."
        }
      ]
    },
    {
      title: "Real-World Blockchain Use Cases",
      duration: "7 min",
      image: "https://images.pexels.com/photos/9577251/pexels-photo-9577251.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      content: [
        {
          type: "text",
          value: "While cryptocurrency is the most well-known application, blockchain technology has potential far beyond digital money. Let's explore some real-world implementations."
        },
        {
          type: "subheading",
          value: "Supply Chain Tracking"
        },
        {
          type: "text",
          value: "Walmart uses blockchain to track food products from farm to store. When there's a contamination issue, they can trace the source in seconds instead of days. IBM's Food Trust network tracks products for major retailers globally."
        },
        {
          type: "subheading",
          value: "Digital Identity"
        },
        {
          type: "text",
          value: "Countries like Estonia have implemented blockchain-based digital identity systems. Citizens can securely prove their identity, sign documents, and access government services — all verified on blockchain."
        },
        {
          type: "subheading",
          value: "Healthcare Records"
        },
        {
          type: "text",
          value: "Blockchain enables patients to own and control their medical records while giving authorized providers secure access. This solves the problem of fragmented records across different healthcare systems."
        },
        {
          type: "subheading",
          value: "Cross-Border Payments"
        },
        {
          type: "text",
          value: "Traditional international wire transfers take 3-5 days and cost $25-50. Blockchain-based solutions like Ripple (XRP) can settle cross-border payments in seconds for fractions of a cent."
        },
        {
          type: "list",
          title: "Other Applications",
          items: [
            "Real estate: Recording property deeds and enabling fractional ownership",
            "Music & Entertainment: Fair royalty distribution to artists",
            "Voting: Secure, transparent, and auditable elections",
            "Credentials: Verifying educational degrees and certifications",
            "Gaming: True ownership of in-game items"
          ]
        }
      ]
    },
    {
      title: "The Future of Blockchain",
      duration: "6 min",
      image: "https://images.unsplash.com/photo-1635237755017-18a7f122a052?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwzfHxldGhlcmV1bSUyMGRlZmklMjBzbWFydCUyMGNvbnRyYWN0cyUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzcxODQ1NjQxfDA&ixlib=rb-4.1.0&q=85",
      content: [
        {
          type: "text",
          value: "Blockchain technology is still in its early stages, comparable to the internet in the early 1990s. Significant developments are on the horizon that could dramatically expand its capabilities and adoption."
        },
        {
          type: "subheading",
          value: "Scalability Solutions"
        },
        {
          type: "text",
          value: "Current blockchains struggle with transaction throughput. Bitcoin processes ~7 transactions per second; Visa handles 65,000+. Layer 2 solutions, sharding, and new consensus mechanisms are being developed to solve this."
        },
        {
          type: "subheading",
          value: "Interoperability"
        },
        {
          type: "text",
          value: "Different blockchains currently operate in silos. Projects like Polkadot, Cosmos, and cross-chain bridges are working to enable seamless communication and value transfer between different networks."
        },
        {
          type: "subheading",
          value: "Central Bank Digital Currencies (CBDCs)"
        },
        {
          type: "text",
          value: "Over 100 countries are exploring CBDCs — digital versions of their national currencies. China's digital yuan is already in use. While not decentralized, these bring blockchain concepts to mainstream finance."
        },
        {
          type: "highlight",
          title: "Looking Ahead",
          value: "By 2030, analysts predict blockchain could account for 10% of global GDP. The technology will likely become invisible to end users — just as we don't think about 'using the internet' when we send an email."
        },
        {
          type: "tip",
          title: "Key Takeaway",
          value: "Understanding blockchain now is like understanding the internet in 1995. It might seem technical and niche today, but it's likely to fundamentally reshape how we organize society, commerce, and trust."
        }
      ]
    }
  ],
  "wallet-security": [
    {
      title: "Understanding Public & Private Keys",
      duration: "8 min",
      image: "https://images.unsplash.com/photo-1767972464040-8bfee42d7bed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwzfHxkaWdpdGFsJTIwc2VjdXJpdHklMjBsb2NrJTIwY3liZXJzZWN1cml0eSUyMHByb3RlY3Rpb258ZW58MHx8fHwxNzcxODQ1NjQ2fDA&ixlib=rb-4.1.0&q=85",
      content: [
        {
          type: "text",
          value: "Understanding public and private keys is fundamental to using cryptocurrency safely. These cryptographic keys are what give you ownership and control of your digital assets."
        },
        {
          type: "highlight",
          title: "The Key Pair",
          value: "Every crypto wallet uses asymmetric cryptography, which creates a mathematically linked pair: a public key (your address) and a private key (your secret password). They're generated together but serve opposite purposes."
        },
        {
          type: "subheading",
          value: "Public Key (Your Address)"
        },
        {
          type: "list",
          title: "Public Key Properties",
          items: [
            "Safe to share with anyone",
            "Used to receive cryptocurrency",
            "Like your email address or bank account number",
            "Derived from your private key mathematically",
            "Cannot be used to reverse-engineer your private key"
          ]
        },
        {
          type: "subheading",
          value: "Private Key (Your Secret)"
        },
        {
          type: "list",
          title: "Private Key Properties",
          items: [
            "NEVER share with anyone",
            "Used to sign transactions and prove ownership",
            "Like the password to your bank account",
            "If someone has this, they control your funds",
            "Lost private key = lost funds forever"
          ]
        },
        {
          type: "example",
          title: "Analogy",
          value: "Think of your public key as your home address — you can share it so people can send you mail (crypto). Your private key is the key to your front door — anyone with it can enter and take everything inside. You'd never give a stranger a copy of your house key!"
        },
        {
          type: "warning",
          value: "The #1 rule in crypto: NEVER share your private key or seed phrase with anyone, ever. No legitimate service will ever ask for it. Anyone who does is trying to steal your funds."
        }
      ]
    },
    {
      title: "Hot Wallets Explained",
      duration: "7 min",
      image: "https://images.unsplash.com/photo-1642662069313-fc20c2c3a7ba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxjcnlwdG8lMjB3YWxsZXQlMjBoYXJkd2FyZSUyMGxlZGdlciUyMHRyZXpvcnxlbnwwfHx8fDE3NzE4NDU5NzZ8MA&ixlib=rb-4.1.0&q=85",
      content: [
        {
          type: "text",
          value: "Hot wallets are cryptocurrency wallets connected to the internet. They offer convenience for everyday transactions but come with higher security risks compared to cold storage."
        },
        {
          type: "highlight",
          title: "Definition",
          value: "A hot wallet is any crypto wallet that's connected to the internet. This includes mobile apps, desktop applications, browser extensions, and exchange wallets. They're called 'hot' because they're always online and ready to transact."
        },
        {
          type: "subheading",
          value: "Types of Hot Wallets"
        },
        {
          type: "list",
          title: "Popular Hot Wallets",
          items: [
            "MetaMask: Browser extension for Ethereum and EVM chains",
            "Trust Wallet: Mobile wallet supporting multiple chains",
            "Exodus: Desktop and mobile wallet with built-in exchange",
            "Coinbase Wallet: Self-custody wallet from Coinbase",
            "Phantom: Popular wallet for Solana"
          ]
        },
        {
          type: "comparison",
          items: [
            {
              aspect: "Convenience",
              traditional: "Very high - instant access",
              crypto: "Very high"
            },
            {
              aspect: "Security",
              traditional: "Lower - vulnerable to online attacks",
              crypto: "Lower"
            },
            {
              aspect: "Best For",
              traditional: "Daily transactions, small amounts",
              crypto: "Small amounts"
            }
          ]
        },
        {
          type: "tip",
          title: "Best Practice",
          value: "Think of a hot wallet like your physical wallet — you keep spending money in it, not your life savings. Keep only what you need for regular transactions in hot wallets."
        }
      ]
    },
    {
      title: "Cold Storage Solutions",
      duration: "8 min",
      image: "https://images.unsplash.com/photo-1637597383944-d39bc455d5c0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHw0fHxjcnlwdG8lMjB3YWxsZXQlMjBoYXJkd2FyZSUyMGxlZGdlciUyMHRyZXpvcnxlbnwwfHx8fDE3NzE4NDU5NzZ8MA&ixlib=rb-4.1.0&q=85",
      content: [
        {
          type: "text",
          value: "Cold storage refers to keeping your private keys completely offline, away from any internet-connected device. This is the gold standard for securing significant cryptocurrency holdings."
        },
        {
          type: "highlight",
          title: "Why Cold Storage?",
          value: "If your keys never touch the internet, they can't be hacked remotely. No matter how sophisticated a hacker is, they can't steal what they can't reach. Cold storage is immune to malware, phishing, and remote attacks."
        },
        {
          type: "subheading",
          value: "Hardware Wallets"
        },
        {
          type: "text",
          value: "Hardware wallets are dedicated devices designed for secure key storage. They sign transactions internally, so your private key never leaves the device. Even if your computer is compromised, your funds remain safe."
        },
        {
          type: "list",
          title: "Popular Hardware Wallets",
          items: [
            "Ledger Nano S/X: Most popular, supports 5,500+ coins",
            "Trezor Model T/One: Open-source, strong security track record",
            "Coldcard: Bitcoin-only, extreme security features",
            "BitBox02: Swiss-made, simple and secure"
          ]
        },
        {
          type: "subheading",
          value: "Paper Wallets"
        },
        {
          type: "text",
          value: "A paper wallet is simply your private key or seed phrase written on paper. While technically cold storage, paper wallets have drawbacks: they're fragile, can be damaged, and require careful handling to spend funds."
        },
        {
          type: "tip",
          title: "Investment Threshold",
          value: "A good rule: if your crypto holdings exceed the cost of a hardware wallet (typically $50-$150), it's worth investing in one. Would you keep $10,000 cash in an unlocked drawer?"
        }
      ]
    },
    {
      title: "Seed Phrase Protection",
      duration: "7 min",
      image: "https://images.unsplash.com/photo-1768839720936-87ce3adf2d08?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxkaWdpdGFsJTIwc2VjdXJpdHklMjBsb2NrJTIwY3liZXJzZWN1cml0eSUyMHByb3RlY3Rpb258ZW58MHx8fHwxNzcxODQ1NjQ2fDA&ixlib=rb-4.1.0&q=85",
      content: [
        {
          type: "text",
          value: "Your seed phrase (also called recovery phrase or mnemonic) is the master key to all your cryptocurrency. It's the most critical piece of information in your crypto security."
        },
        {
          type: "highlight",
          title: "What is a Seed Phrase?",
          value: "A seed phrase is a sequence of 12-24 random words that can regenerate all your private keys and recover all your wallets. It's typically generated when you first create a wallet and should be written down immediately."
        },
        {
          type: "warning",
          value: "If someone gets your seed phrase, they can steal ALL your funds from ALL accounts associated with it. If you lose your seed phrase and your wallet fails, your funds are lost FOREVER. There is no recovery option."
        },
        {
          type: "steps",
          items: [
            {
              step: 1,
              title: "Write It Down Physically",
              description: "Never type your seed phrase on a computer or phone. Write it on paper by hand. Double-check every word and their order."
            },
            {
              step: 2,
              title: "Make Multiple Copies",
              description: "Store copies in different secure locations. A fire or flood shouldn't destroy all your backups."
            },
            {
              step: 3,
              title: "Consider Metal Backup",
              description: "Paper can burn, fade, or get water damaged. Metal backup plates (like Cryptosteel) can survive fires and floods."
            },
            {
              step: 4,
              title: "Store Securely",
              description: "Use a safe, safety deposit box, or other secure location. Don't keep it anywhere obvious."
            },
            {
              step: 5,
              title: "Never Store Digitally",
              description: "No photos, no cloud storage, no notes apps, no emails. Digital storage can be hacked."
            }
          ]
        },
        {
          type: "tip",
          title: "Advanced: Split Storage",
          value: "For large holdings, consider splitting your seed phrase into parts stored in different locations. You'd need multiple parts to reconstruct it, adding another layer of security."
        }
      ]
    },
    {
      title: "Two-Factor Authentication",
      duration: "6 min",
      image: "https://images.unsplash.com/photo-1768839721176-2fa91fdce725?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxkaWdpdGFsJTIwc2VjdXJpdHklMjBsb2NrJTIwY3liZXJzZWN1cml0eSUyMHByb3RlY3Rpb258ZW58MHx8fHwxNzcxODQ1NjQ2fDA&ixlib=rb-4.1.0&q=85",
      content: [
        {
          type: "text",
          value: "Two-Factor Authentication (2FA) adds an extra layer of security beyond just a password. Even if someone steals your password, they can't access your account without the second factor."
        },
        {
          type: "highlight",
          title: "How 2FA Works",
          value: "2FA requires two different types of verification: something you know (password) and something you have (phone, hardware key). This means a hacker would need to compromise both to access your account."
        },
        {
          type: "subheading",
          value: "Types of 2FA (from weakest to strongest)"
        },
        {
          type: "list",
          title: "2FA Methods Ranked",
          items: [
            "SMS (Text Message): Weakest - vulnerable to SIM swapping attacks",
            "Email: Weak - depends on email security",
            "Authenticator App: Strong - Google Authenticator, Authy",
            "Hardware Key: Strongest - YubiKey, Trezor"
          ]
        },
        {
          type: "warning",
          value: "AVOID SMS 2FA for crypto accounts. Hackers can call your phone company, impersonate you, and transfer your number to their SIM (SIM swapping). This has led to millions in losses."
        },
        {
          type: "steps",
          items: [
            {
              step: 1,
              title: "Enable 2FA Everywhere",
              description: "Turn on 2FA for every crypto exchange, wallet, and related email account."
            },
            {
              step: 2,
              title: "Use Authenticator Apps",
              description: "Download Google Authenticator or Authy. Scan the QR code provided by the service."
            },
            {
              step: 3,
              title: "Save Backup Codes",
              description: "Most services provide backup codes. Store these safely in case you lose your phone."
            }
          ]
        }
      ]
    },
    {
      title: "Common Scams to Avoid",
      duration: "8 min",
      image: "https://images.pexels.com/photos/8370332/pexels-photo-8370332.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      content: [
        {
          type: "text",
          value: "The crypto space unfortunately attracts many scammers. Being aware of common tactics can help you avoid becoming a victim. Remember: if it sounds too good to be true, it definitely is."
        },
        {
          type: "subheading",
          value: "1. Phishing Attacks"
        },
        {
          type: "text",
          value: "Scammers create fake websites, emails, or social media accounts that look identical to legitimate services. They trick you into entering your credentials or seed phrase on their fake site."
        },
        {
          type: "warning",
          value: "Always double-check URLs. Scammers use tricks like 'metamask.io' vs 'metamäsk.io' (different character) or 'coinbase-secure.com' (fake domain)."
        },
        {
          type: "subheading",
          value: "2. Giveaway Scams"
        },
        {
          type: "text",
          value: "'Send 0.1 BTC and receive 1 BTC back!' These scams often impersonate celebrities like Elon Musk. No legitimate person or company will ever ask you to send crypto to receive more back."
        },
        {
          type: "subheading",
          value: "3. Rug Pulls"
        },
        {
          type: "text",
          value: "Developers create a new token, hype it up, attract investors, then suddenly withdraw all liquidity and disappear with the funds. Always research projects and teams before investing."
        },
        {
          type: "subheading",
          value: "4. Romance/Pig Butchering Scams"
        },
        {
          type: "text",
          value: "Scammers build relationships over weeks or months, then convince victims to invest in fake crypto platforms. These are extremely sophisticated and target emotional vulnerability."
        },
        {
          type: "list",
          title: "Red Flags to Watch For",
          items: [
            "Guaranteed returns or profits",
            "Pressure to act quickly",
            "Unsolicited investment advice",
            "Requests for your private keys or seed phrase",
            "Celebrity endorsements (usually fake)",
            "Too-good-to-be-true opportunities"
          ]
        }
      ]
    },
    {
      title: "Best Security Practices",
      duration: "6 min",
      image: "https://images.unsplash.com/photo-1642662069292-1fc53175a328?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwyfHxjcnlwdG8lMjB3YWxsZXQlMjBoYXJkd2FyZSUyMGxlZGdlciUyMHRyZXpvcnxlbnwwfHx8fDE3NzE4NDU5NzZ8MA&ixlib=rb-4.1.0&q=85",
      content: [
        {
          type: "text",
          value: "Let's consolidate everything into a security checklist you can follow to protect your cryptocurrency holdings."
        },
        {
          type: "steps",
          items: [
            {
              step: 1,
              title: "Use Strong, Unique Passwords",
              description: "Every crypto account should have a unique password of 16+ characters. Use a password manager like 1Password or Bitwarden."
            },
            {
              step: 2,
              title: "Enable 2FA Everywhere",
              description: "Use authenticator apps (not SMS) for all exchanges and related accounts, including email."
            },
            {
              step: 3,
              title: "Secure Your Seed Phrase",
              description: "Write it on paper, make multiple copies, store in secure locations, never store digitally."
            },
            {
              step: 4,
              title: "Use Hardware Wallets for Significant Holdings",
              description: "If you have more than $500-1000 in crypto, invest in a hardware wallet."
            },
            {
              step: 5,
              title: "Verify Everything",
              description: "Double-check addresses before sending. Verify website URLs. Be skeptical of unsolicited messages."
            },
            {
              step: 6,
              title: "Keep Software Updated",
              description: "Update your wallet apps, operating system, and browser regularly. Updates often fix security vulnerabilities."
            },
            {
              step: 7,
              title: "Use Dedicated Devices",
              description: "Consider using a dedicated computer or phone for crypto transactions, free from other apps and potential malware."
            }
          ]
        },
        {
          type: "highlight",
          title: "The Ultimate Rule",
          value: "In crypto, you are your own bank. This is empowering but also means you're responsible for security. There's no customer service to call if you make a mistake. Take security seriously."
        },
        {
          type: "tip",
          title: "Test First",
          value: "When sending to a new address, always send a small test amount first. Verify it arrives correctly before sending larger amounts. The small fee is worth the peace of mind."
        }
      ]
    }
  ]
};

// Add default lessons for courses without detailed content
const generateDefaultLessons = (course) => {
  return course.topics.map((topic, index) => ({
    title: topic,
    duration: `${5 + Math.floor(Math.random() * 5)} min`,
    image: course.image,
    content: [
      {
        type: "text",
        value: `This lesson covers ${topic.toLowerCase()} in detail. Understanding this concept is essential for your cryptocurrency journey.`
      },
      {
        type: "highlight",
        title: "Key Concept",
        value: `${topic} is an important aspect of ${course.title.toLowerCase()}. Pay close attention to the fundamentals covered here.`
      },
      {
        type: "text",
        value: "As you progress through this course, you'll build a comprehensive understanding of this topic and how it connects to other concepts in the cryptocurrency ecosystem."
      },
      {
        type: "tip",
        title: "Pro Tip",
        value: "Take notes as you learn and revisit this lesson if you need a refresher. Building a strong foundation will help you understand more advanced topics later."
      }
    ]
  }));
};

// ============================================
// COURSE DATA
// ============================================

const COURSES = [
  {
    id: "crypto-basics",
    title: "Cryptocurrency Fundamentals",
    description: "Learn what cryptocurrency is, how it works, and why it matters in today's digital economy.",
    image: "https://images.unsplash.com/photo-1635840420248-1ba20fd1aac2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHxjcnlwdG9jdXJyZW5jeSUyMGJsb2NrY2hhaW4lMjBlZHVjYXRpb24lMjBsZWFybmluZ3xlbnwwfHx8fDE3NzE4NDU2Mjd8MA&ixlib=rb-4.1.0&q=85",
    level: "Beginner",
    duration: "45 min",
    lessons: 6,
    category: "basics",
    featured: true,
    topics: ["What is Cryptocurrency", "History of Bitcoin", "Types of Cryptocurrencies", "How Transactions Work", "Crypto vs Traditional Money", "Getting Started"]
  },
  {
    id: "blockchain-101",
    title: "Blockchain Technology 101",
    description: "Understand the revolutionary technology behind cryptocurrencies and its real-world applications.",
    image: "https://images.pexels.com/photos/8369659/pexels-photo-8369659.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    level: "Beginner",
    duration: "1 hour",
    lessons: 8,
    category: "blockchain",
    featured: true,
    topics: ["What is Blockchain", "How Blocks Work", "Decentralization Explained", "Consensus Mechanisms", "Public vs Private Blockchains", "Smart Contracts Intro", "Real-World Use Cases", "Future of Blockchain"]
  },
  {
    id: "wallet-security",
    title: "Crypto Wallets & Security",
    description: "Master the essentials of storing your crypto safely with different wallet types and security practices.",
    image: "https://images.unsplash.com/photo-1767972464040-8bfee42d7bed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwzfHxkaWdpdGFsJTIwc2VjdXJpdHklMjBsb2NrJTIwY3liZXJzZWN1cml0eSUyMHByb3RlY3Rpb258ZW58MHx8fHwxNzcxODQ1NjQ2fDA&ixlib=rb-4.1.0&q=85",
    level: "Beginner",
    duration: "50 min",
    lessons: 7,
    category: "security",
    featured: false,
    topics: ["Public & Private Keys", "Hot Wallets Explained", "Cold Storage Solutions", "Seed Phrase Protection", "Two-Factor Authentication", "Common Scams to Avoid", "Best Security Practices"]
  },
  {
    id: "bitcoin-deep-dive",
    title: "Bitcoin Deep Dive",
    description: "Go beyond the basics and understand Bitcoin's technical architecture, mining, and economic model.",
    image: "https://images.unsplash.com/photo-1673571829333-c1c93414256a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxiaXRjb2luJTIwdHJhZGluZyUyMGludmVzdG1lbnQlMjBmaW5hbmNlfGVufDB8fHx8MTc3MTg0NTYzN3ww&ixlib=rb-4.1.0&q=85",
    level: "Intermediate",
    duration: "1.5 hours",
    lessons: 10,
    category: "bitcoin",
    featured: true,
    topics: ["Bitcoin's Technical Design", "Mining & Proof of Work", "Halving Events", "Lightning Network", "Bitcoin Economics", "Store of Value Thesis", "Institutional Adoption", "Bitcoin Governance", "Future Developments", "Investment Considerations"]
  },
  {
    id: "ethereum-ecosystem",
    title: "Ethereum & Smart Contracts",
    description: "Explore the Ethereum blockchain, smart contracts, and the foundation of DeFi and NFTs.",
    image: "https://images.unsplash.com/photo-1624956001740-f4ff24a5ef42?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxldGhlcmV1bSUyMGRlZmklMjBzbWFydCUyMGNvbnRyYWN0cyUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzcxODQ1NjQxfDA&ixlib=rb-4.1.0&q=85",
    level: "Intermediate",
    duration: "2 hours",
    lessons: 12,
    category: "ethereum",
    featured: false,
    topics: ["Ethereum vs Bitcoin", "Smart Contract Basics", "Gas Fees Explained", "ERC-20 Tokens", "Proof of Stake", "Layer 2 Solutions", "DeFi Introduction", "NFT Fundamentals", "DAOs Explained", "Ethereum Roadmap", "Development Basics", "Ecosystem Overview"]
  },
  {
    id: "trading-fundamentals",
    title: "Crypto Trading Fundamentals",
    description: "Learn essential trading concepts, chart analysis, and risk management strategies for crypto markets.",
    image: "https://images.unsplash.com/photo-1767424196045-030bbde122a4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHx0cmFkaW5nJTIwY2hhcnQlMjBjYW5kbGVzdGljayUyMHN0b2NrJTIwbWFya2V0JTIwYW5hbHlzaXN8ZW58MHx8fHwxNzcxODQ1OTg0fDA&ixlib=rb-4.1.0&q=85",
    level: "Intermediate",
    duration: "2.5 hours",
    lessons: 15,
    category: "trading",
    featured: false,
    topics: ["Market Orders vs Limit Orders", "Reading Candlestick Charts", "Support & Resistance", "Volume Analysis", "Moving Averages", "RSI & MACD", "Risk Management", "Position Sizing", "Dollar Cost Averaging", "Portfolio Allocation", "Tax Considerations", "Exchange Selection", "Trading Psychology", "Common Mistakes", "Building a Strategy"]
  },
  {
    id: "defi-mastery",
    title: "DeFi Mastery",
    description: "Master decentralized finance protocols, yield farming, liquidity provision, and advanced DeFi strategies.",
    image: "https://images.unsplash.com/photo-1635237755017-18a7f122a052?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwzfHxldGhlcmV1bSUyMGRlZmklMjBzbWFydCUyMGNvbnRyYWN0cyUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzcxODQ1NjQxfDA&ixlib=rb-4.1.0&q=85",
    level: "Advanced",
    duration: "3 hours",
    lessons: 18,
    category: "defi",
    featured: false,
    topics: ["DeFi Architecture", "Liquidity Pools", "Automated Market Makers", "Yield Farming Strategies", "Impermanent Loss", "Lending Protocols", "Stablecoin Mechanics", "Flash Loans", "Cross-Chain DeFi", "DeFi Risks", "Protocol Analysis", "Governance Tokens", "Yield Optimization", "Gas Optimization", "Security Audits", "DeFi Aggregators", "Portfolio Tools", "Future Trends"]
  },
  {
    id: "technical-analysis",
    title: "Advanced Technical Analysis",
    description: "Professional-level chart patterns, indicators, and trading strategies used by experienced traders.",
    image: "https://images.unsplash.com/photo-1768055104929-cf2317674a80?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwyfHx0cmFkaW5nJTIwY2hhcnQlMjBjYW5kbGVzdGljayUyMHN0b2NrJTIwbWFya2V0JTIwYW5hbHlzaXN8ZW58MHx8fHwxNzcxODQ1OTg0fDA&ixlib=rb-4.1.0&q=85",
    level: "Advanced",
    duration: "4 hours",
    lessons: 20,
    category: "trading",
    featured: false,
    topics: ["Advanced Chart Patterns", "Fibonacci Retracements", "Elliott Wave Theory", "Ichimoku Cloud", "Volume Profile", "Order Flow Analysis", "Market Structure", "Liquidity Concepts", "Wyckoff Method", "Harmonic Patterns", "Multi-Timeframe Analysis", "Divergence Trading", "Breakout Strategies", "Mean Reversion", "Momentum Trading", "Algorithmic Concepts", "Backtesting", "Risk Metrics", "Psychology Deep Dive", "Professional Tools"]
  },
  {
    id: "crypto-security-advanced",
    title: "Advanced Crypto Security",
    description: "Enterprise-level security practices, multi-sig setups, and protecting large crypto holdings.",
    image: "https://images.unsplash.com/photo-1768839720936-87ce3adf2d08?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxkaWdpdGFsJTIwc2VjdXJpdHklMjBsb2NrJTIwY3liZXJzZWN1cml0eSUyMHByb3RlY3Rpb258ZW58MHx8fHwxNzcxODQ1NjQ2fDA&ixlib=rb-4.1.0&q=85",
    level: "Advanced",
    duration: "2 hours",
    lessons: 12,
    category: "security",
    featured: false,
    topics: ["Multi-Signature Wallets", "Hardware Wallet Deep Dive", "Air-Gapped Security", "Inheritance Planning", "Corporate Custody", "Insurance Options", "Operational Security", "Social Engineering Defense", "Smart Contract Auditing", "Exchange Security", "Privacy Techniques", "Disaster Recovery"]
  }
];

const TRACKS = [
  {
    id: "beginner",
    title: "Beginner Track",
    description: "Perfect for those new to cryptocurrency. Start your journey here.",
    icon: BookOpen,
    color: "emerald",
    courses: ["crypto-basics", "blockchain-101", "wallet-security"]
  },
  {
    id: "intermediate",
    title: "Intermediate Track",
    description: "Ready to go deeper? Expand your knowledge with these courses.",
    icon: TrendingUp,
    color: "blue",
    courses: ["bitcoin-deep-dive", "ethereum-ecosystem", "trading-fundamentals"]
  },
  {
    id: "advanced",
    title: "Advanced Track",
    description: "For experienced users ready to master complex topics.",
    icon: Award,
    color: "purple",
    courses: ["defi-mastery", "technical-analysis", "crypto-security-advanced"]
  }
];

// ============================================
// LESSON VIEWER COMPONENT
// ============================================

const LessonViewer = ({ course, onClose }) => {
  const [currentLesson, setCurrentLesson] = useState(0);
  const [completedLessons, setCompletedLessons] = useState([]);
  const mainContentRef = useRef(null);

  // Get lessons for this course
  const lessons = LESSON_CONTENT[course.id] || generateDefaultLessons(course);
  const lesson = lessons[currentLesson];
  const progress = ((currentLesson + 1) / lessons.length) * 100;

  // Scroll to top of main content
  const scrollToTop = () => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollTo({ top: 0, behavior: 'instant' });
    }
  };

  const handleNext = () => {
    if (!completedLessons.includes(currentLesson)) {
      setCompletedLessons([...completedLessons, currentLesson]);
    }
    if (currentLesson < lessons.length - 1) {
      setCurrentLesson(currentLesson + 1);
      scrollToTop();
    }
  };

  const handlePrevious = () => {
    if (currentLesson > 0) {
      setCurrentLesson(currentLesson - 1);
      scrollToTop();
    }
  };

  const handleLessonSelect = (index) => {
    setCurrentLesson(index);
    scrollToTop();
  };

  // Render content block based on type
  const renderContent = (block, index) => {
    switch (block.type) {
      case "text":
        return (
          <p key={index} className="text-blue-100/80 leading-relaxed mb-4">
            {block.value}
          </p>
        );
      
      case "subheading":
        return (
          <h3 key={index} className="text-xl font-bold text-white mt-8 mb-4">
            {block.value}
          </h3>
        );
      
      case "highlight":
        return (
          <div key={index} className="bg-[#3B82F6]/10 border border-[#3B82F6]/30 rounded-xl p-5 my-6">
            <div className="flex items-center gap-2 mb-2">
              <Info className="w-5 h-5 text-[#3B82F6]" />
              <h4 className="font-semibold text-[#3B82F6]">{block.title}</h4>
            </div>
            <p className="text-blue-100/80">{block.value}</p>
          </div>
        );
      
      case "warning":
        return (
          <div key={index} className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-5 my-6">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              <h4 className="font-semibold text-amber-400">Important Warning</h4>
            </div>
            <p className="text-blue-100/80">{block.value}</p>
          </div>
        );
      
      case "tip":
        return (
          <div key={index} className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-5 my-6">
            <div className="flex items-center gap-2 mb-2">
              <Lightbulb className="w-5 h-5 text-emerald-400" />
              <h4 className="font-semibold text-emerald-400">{block.title || "Pro Tip"}</h4>
            </div>
            <p className="text-blue-100/80">{block.value}</p>
          </div>
        );
      
      case "example":
        return (
          <div key={index} className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-5 my-6">
            <div className="flex items-center gap-2 mb-2">
              <Lightbulb className="w-5 h-5 text-purple-400" />
              <h4 className="font-semibold text-purple-400">{block.title}</h4>
            </div>
            <p className="text-blue-100/80 whitespace-pre-line">{block.value}</p>
          </div>
        );
      
      case "image":
        return (
          <figure key={index} className="my-8">
            <img 
              src={block.url} 
              alt={block.caption}
              className="w-full rounded-xl border border-white/10"
            />
            {block.caption && (
              <figcaption className="text-center text-blue-100/50 text-sm mt-3">
                {block.caption}
              </figcaption>
            )}
          </figure>
        );
      
      case "list":
        return (
          <div key={index} className="my-6">
            {block.title && (
              <h4 className="font-semibold text-white mb-3">{block.title}</h4>
            )}
            <ul className="space-y-2">
              {block.items.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#3B82F6] flex-shrink-0 mt-0.5" />
                  <span className="text-blue-100/80">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        );
      
      case "steps":
        return (
          <div key={index} className="my-8 space-y-4">
            {block.items.map((item, i) => (
              <div key={i} className="flex gap-4 p-4 bg-white/5 rounded-xl">
                <div className="w-10 h-10 rounded-full bg-[#3B82F6] flex items-center justify-center font-bold text-white flex-shrink-0">
                  {item.step}
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">{item.title}</h4>
                  <p className="text-blue-100/70 text-sm">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        );
      
      case "timeline":
        return (
          <div key={index} className="my-8 space-y-3">
            {block.items.map((item, i) => (
              <div key={i} className="flex gap-4 items-start">
                <div className="w-16 flex-shrink-0 text-[#3B82F6] font-bold">
                  {item.year}
                </div>
                <div className="flex-1 p-3 bg-white/5 rounded-lg border-l-2 border-[#3B82F6]">
                  <p className="text-blue-100/80 text-sm">{item.event}</p>
                </div>
              </div>
            ))}
          </div>
        );
      
      case "comparison":
        return (
          <div key={index} className="my-6 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left p-3 text-white">Aspect</th>
                  <th className="text-left p-3 text-blue-100/70">Traditional</th>
                  <th className="text-left p-3 text-[#3B82F6]">Crypto</th>
                </tr>
              </thead>
              <tbody>
                {block.items.map((item, i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td className="p-3 text-white font-medium">{item.aspect}</td>
                    <td className="p-3 text-blue-100/60">{item.traditional}</td>
                    <td className="p-3 text-blue-100/80">{item.crypto}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#001435] overflow-hidden" data-testid="lesson-viewer">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-[#001020] border-b border-white/10 z-10">
        <div className="flex items-center justify-between px-4 py-3">
          <button 
            onClick={onClose}
            className="flex items-center gap-2 text-blue-100/70 hover:text-white transition-colors"
            data-testid="close-lesson-btn"
          >
            <X className="w-5 h-5" />
            <span className="hidden sm:inline">Exit Course</span>
          </button>
          
          <div className="flex-1 mx-4 max-w-xl">
            <div className="text-white font-medium text-sm truncate text-center">
              {course.title}
            </div>
            <div className="mt-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div 
                className="h-full bg-[#3B82F6] transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
          
          <div className="text-blue-100/50 text-sm">
            {currentLesson + 1} / {lessons.length}
          </div>
        </div>
      </header>

      <div className="flex h-full pt-14">
        {/* Sidebar - Lesson List */}
        <aside className="hidden lg:block w-80 bg-[#001020] border-r border-white/10 overflow-y-auto">
          <div className="p-4">
            <h3 className="text-white font-semibold mb-4">Course Content</h3>
            <div className="space-y-2">
              {lessons.map((l, index) => (
                <button
                  key={index}
                  onClick={() => handleLessonSelect(index)}
                  className={`w-full text-left p-3 rounded-lg transition-colors flex items-start gap-3 ${
                    currentLesson === index
                      ? "bg-[#3B82F6]/20 border border-[#3B82F6]/30"
                      : "hover:bg-white/5"
                  }`}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                    completedLessons.includes(index)
                      ? "bg-emerald-500 text-white"
                      : currentLesson === index
                        ? "bg-[#3B82F6] text-white"
                        : "bg-white/10 text-blue-100/50"
                  }`}>
                    {completedLessons.includes(index) ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <span className="text-xs">{index + 1}</span>
                    )}
                  </div>
                  <div>
                    <div className={`text-sm font-medium ${
                      currentLesson === index ? "text-white" : "text-blue-100/80"
                    }`}>
                      {l.title}
                    </div>
                    <div className="text-xs text-blue-100/50 mt-1 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {l.duration}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main ref={mainContentRef} className="flex-1 overflow-y-auto pb-24">
          <div className="max-w-3xl mx-auto px-4 py-8">
            {/* Lesson Header */}
            <div className="mb-8">
              <div className="flex items-center gap-2 text-[#3B82F6] text-sm mb-2">
                <span>Lesson {currentLesson + 1}</span>
                <span>•</span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {lesson.duration}
                </span>
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                {lesson.title}
              </h1>
            </div>

            {/* Lesson Image */}
            {lesson.image && (
              <img 
                src={lesson.image}
                alt={lesson.title}
                className="w-full h-64 object-cover rounded-2xl mb-8 border border-white/10"
              />
            )}

            {/* Lesson Content */}
            <div className="prose prose-invert max-w-none">
              {lesson.content.map((block, index) => renderContent(block, index))}
            </div>
          </div>
        </main>
      </div>

      {/* Bottom Navigation */}
      <footer className="fixed bottom-0 left-0 right-0 bg-[#001020] border-t border-white/10 p-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-4">
          <button
            onClick={handlePrevious}
            disabled={currentLesson === 0}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl transition-colors ${
              currentLesson === 0
                ? "bg-white/5 text-blue-100/30 cursor-not-allowed"
                : "bg-white/10 text-white hover:bg-white/20"
            }`}
            data-testid="prev-lesson-btn"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Previous</span>
          </button>

          <div className="flex-1 flex justify-center">
            <div className="flex gap-1">
              {lessons.map((_, index) => (
                <button
                  key={index}
                  onClick={() => handleLessonSelect(index)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    currentLesson === index
                      ? "bg-[#3B82F6]"
                      : completedLessons.includes(index)
                        ? "bg-emerald-500"
                        : "bg-white/20"
                  }`}
                />
              ))}
            </div>
          </div>

          {currentLesson < lessons.length - 1 ? (
            <button
              onClick={handleNext}
              className="flex items-center gap-2 px-6 py-3 rounded-xl bg-[#3B82F6] hover:bg-[#2563EB] text-white transition-colors"
              data-testid="next-lesson-btn"
            >
              <span className="hidden sm:inline">Next Lesson</span>
              <ChevronRight className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={onClose}
              className="flex items-center gap-2 px-6 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white transition-colors"
              data-testid="complete-course-btn"
            >
              <Award className="w-5 h-5" />
              <span className="hidden sm:inline">Complete Course</span>
            </button>
          )}
        </div>
      </footer>
    </div>
  );
};

// ============================================
// COMPONENT DEFINITIONS
// ============================================

const CourseCard = ({ course, onClick }) => {
  const levelColors = {
    Beginner: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    Intermediate: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    Advanced: "bg-purple-500/20 text-purple-400 border-purple-500/30"
  };

  return (
    <div 
      onClick={() => onClick(course)}
      className="group cursor-pointer bg-gradient-to-br from-[#001a40] to-[#002255] rounded-2xl overflow-hidden border border-white/10 hover:border-[#3B82F6]/50 transition-all duration-300 hover:shadow-xl hover:shadow-[#3B82F6]/10 hover:-translate-y-1"
      data-testid={`course-card-${course.id}`}
    >
      <div className="relative h-44 overflow-hidden">
        <img 
          src={course.image} 
          alt={course.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#001435] via-transparent to-transparent" />
        
        <div className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-semibold border ${levelColors[course.level]}`}>
          {course.level}
        </div>
        
        {course.featured && (
          <div className="absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-semibold bg-amber-500/20 text-amber-400 border border-amber-500/30 flex items-center gap-1">
            <Star className="w-3 h-3" />
            Featured
          </div>
        )}
      </div>
      
      <div className="p-5">
        <h3 className="text-lg font-bold text-white mb-2 group-hover:text-[#3B82F6] transition-colors line-clamp-2">
          {course.title}
        </h3>
        <p className="text-blue-100/60 text-sm mb-4 line-clamp-2">
          {course.description}
        </p>
        
        <div className="flex items-center justify-between text-xs text-blue-100/50">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              {course.duration}
            </span>
            <span className="flex items-center gap-1">
              <BookOpen className="w-3.5 h-3.5" />
              {course.lessons} lessons
            </span>
          </div>
          <Play className="w-5 h-5 text-[#3B82F6] group-hover:scale-110 transition-transform" />
        </div>
      </div>
    </div>
  );
};

const TrackCard = ({ track, onSelect }) => {
  const Icon = track.icon;
  const colorClasses = {
    emerald: "from-emerald-500/20 to-emerald-500/5 border-emerald-500/30 hover:border-emerald-500/50",
    blue: "from-blue-500/20 to-blue-500/5 border-blue-500/30 hover:border-blue-500/50",
    purple: "from-purple-500/20 to-purple-500/5 border-purple-500/30 hover:border-purple-500/50"
  };
  const iconColors = {
    emerald: "text-emerald-400",
    blue: "text-blue-400",
    purple: "text-purple-400"
  };

  return (
    <button
      onClick={() => onSelect(track.id)}
      className={`w-full p-6 rounded-2xl bg-gradient-to-br ${colorClasses[track.color]} border transition-all duration-300 hover:-translate-y-1 text-left group`}
      data-testid={`track-${track.id}`}
    >
      <div className="flex items-start gap-4">
        <div className={`w-14 h-14 rounded-xl bg-white/10 flex items-center justify-center ${iconColors[track.color]} flex-shrink-0`}>
          <Icon className="w-7 h-7" />
        </div>
        <div className="flex-1">
          <h3 className="text-xl font-bold text-white mb-2 group-hover:text-[#3B82F6] transition-colors">
            {track.title}
          </h3>
          <p className="text-blue-100/60 text-sm mb-3">
            {track.description}
          </p>
          <div className="flex items-center gap-2 text-xs text-blue-100/50">
            <span>{track.courses.length} courses</span>
            <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </div>
    </button>
  );
};

const CourseModal = ({ course, onClose, onStartCourse }) => {
  if (!course) return null;

  const levelColors = {
    Beginner: "bg-emerald-500 text-white",
    Intermediate: "bg-blue-500 text-white",
    Advanced: "bg-purple-500 text-white"
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="course-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative bg-gradient-to-br from-[#001a40] to-[#001435] rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-white/10">
        <div className="relative h-56 overflow-hidden">
          <img src={course.image} alt={course.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#001435] via-[#001435]/50 to-transparent" />
          
          <button onClick={onClose} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
          
          <div className={`absolute bottom-4 left-6 px-4 py-2 rounded-full text-sm font-semibold ${levelColors[course.level]}`}>
            {course.level}
          </div>
        </div>
        
        <div className="p-6 sm:p-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">{course.title}</h2>
          <p className="text-blue-100/70 mb-6 leading-relaxed">{course.description}</p>
          
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-white/5 rounded-xl p-4 text-center">
              <Clock className="w-6 h-6 text-[#3B82F6] mx-auto mb-2" />
              <div className="text-white font-semibold">{course.duration}</div>
              <div className="text-blue-100/50 text-xs">Duration</div>
            </div>
            <div className="bg-white/5 rounded-xl p-4 text-center">
              <BookOpen className="w-6 h-6 text-[#3B82F6] mx-auto mb-2" />
              <div className="text-white font-semibold">{course.lessons}</div>
              <div className="text-blue-100/50 text-xs">Lessons</div>
            </div>
            <div className="bg-white/5 rounded-xl p-4 text-center">
              <Award className="w-6 h-6 text-[#3B82F6] mx-auto mb-2" />
              <div className="text-white font-semibold">Free</div>
              <div className="text-blue-100/50 text-xs">Certificate</div>
            </div>
          </div>
          
          <div className="mb-8">
            <h3 className="text-lg font-bold text-white mb-4">What You'll Learn</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {course.topics.map((topic, index) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-white/5 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span className="text-blue-100/80 text-sm">{topic}</span>
                </div>
              ))}
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <button 
              onClick={() => onStartCourse(course)}
              className="flex-1 flex items-center justify-center gap-2 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold py-4 rounded-xl transition-colors"
              data-testid="start-course-btn"
            >
              <Play className="w-5 h-5" />
              Start Course
            </button>
            <button onClick={onClose} className="flex-1 flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-medium py-4 rounded-xl transition-colors">
              Browse More Courses
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const WhyFonderXAcademy = () => {
  const benefits = [
    { icon: BookOpen, title: "Expert-Curated Content", description: "Learn from comprehensive, well-researched material covering all aspects of cryptocurrency." },
    { icon: Layers, title: "Structured Learning Paths", description: "Progress from beginner to advanced with organized tracks tailored to your skill level." },
    { icon: Zap, title: "Self-Paced Learning", description: "Study at your own pace, anywhere, anytime. Return to lessons whenever you need a refresher." },
    { icon: Award, title: "Completely Free", description: "Access all courses and educational materials at no cost. No hidden fees or subscriptions." }
  ];

  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {benefits.map((benefit, index) => {
        const Icon = benefit.icon;
        return (
          <div key={index} className="p-6 bg-gradient-to-br from-white/5 to-transparent rounded-2xl border border-white/10 hover:border-[#3B82F6]/30 transition-colors group">
            <div className="w-14 h-14 rounded-xl bg-[#3B82F6]/20 flex items-center justify-center mb-4 group-hover:bg-[#3B82F6]/30 transition-colors">
              <Icon className="w-7 h-7 text-[#3B82F6]" />
            </div>
            <h3 className="text-lg font-bold text-white mb-2">{benefit.title}</h3>
            <p className="text-blue-100/60 text-sm leading-relaxed">{benefit.description}</p>
          </div>
        );
      })}
    </div>
  );
};

const FilterButtons = ({ activeFilter, onFilterChange }) => {
  const filters = [
    { id: "all", label: "All Courses" },
    { id: "Beginner", label: "Beginner" },
    { id: "Intermediate", label: "Intermediate" },
    { id: "Advanced", label: "Advanced" }
  ];

  return (
    <div className="flex flex-wrap gap-2">
      {filters.map(filter => (
        <button
          key={filter.id}
          onClick={() => onFilterChange(filter.id)}
          className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
            activeFilter === filter.id
              ? "bg-[#3B82F6] text-white"
              : "bg-white/10 text-blue-100/70 hover:bg-white/20 hover:text-white"
          }`}
          data-testid={`filter-${filter.id}`}
        >
          {filter.label}
        </button>
      ))}
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

export default function CryptoAcademy() {
  const [activeFilter, setActiveFilter] = useState("all");
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [activeCourse, setActiveCourse] = useState(null); // For lesson viewer
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const filteredCourses = activeFilter === "all" 
    ? COURSES 
    : COURSES.filter(course => course.level === activeFilter);

  const featuredCourses = COURSES.filter(course => course.featured);

  const handleStartCourse = (course) => {
    if (!isAuthenticated) {
      // Redirect to login, then come back
      navigate('/auth', { state: { from: { pathname: '/crypto/academy' } } });
      return;
    }
    setSelectedCourse(null);
    setActiveCourse(course);
  };

  // If a course is active, show the lesson viewer
  if (activeCourse) {
    return (
      <LessonViewer 
        course={activeCourse} 
        onClose={() => setActiveCourse(null)} 
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#001435]" data-testid="crypto-academy-page">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-16 sm:py-24 px-4">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-96 h-96 bg-[#3B82F6]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl" />
        </div>
        
        <div className="max-w-7xl mx-auto relative">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#3B82F6]/20 text-[#3B82F6] px-4 py-2 rounded-full text-sm font-medium mb-6">
              <GraduationCap className="w-4 h-4" />
              Free Crypto Education
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              FonderX <span className="text-[#3B82F6]">Academy</span>
            </h1>
            
            <p className="text-blue-100/60 text-lg sm:text-xl max-w-3xl mx-auto mb-10">
              Master cryptocurrency and blockchain with our comprehensive, free courses. 
              From beginner fundamentals to advanced trading strategies — learn at your own pace.
            </p>
            
            <div className="flex flex-wrap justify-center gap-8 sm:gap-12 mb-10">
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-white">{COURSES.length}</div>
                <div className="text-blue-100/50 text-sm">Courses</div>
              </div>
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-white">{COURSES.reduce((acc, c) => acc + c.lessons, 0)}+</div>
                <div className="text-blue-100/50 text-sm">Lessons</div>
              </div>
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-white">3</div>
                <div className="text-blue-100/50 text-sm">Learning Tracks</div>
              </div>
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-[#3B82F6]">FREE</div>
                <div className="text-blue-100/50 text-sm">Forever</div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#courses" className="inline-flex items-center justify-center gap-2 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold px-8 py-4 rounded-xl transition-all shadow-lg shadow-[#3B82F6]/25" data-testid="explore-courses-btn">
                <BookOpen className="w-5 h-5" />
                Explore Courses
              </a>
              <Link to="/crypto" className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-medium px-8 py-4 rounded-xl transition-all">
                <TrendingUp className="w-5 h-5" />
                View Live Prices
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Learning Tracks */}
      <section className="py-12 px-4 bg-gradient-to-b from-transparent to-[#001020]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">Choose Your Learning Path</h2>
            <p className="text-blue-100/60 max-w-2xl mx-auto">
              Whether you're just starting or looking to deepen your expertise, we have a track for you.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {TRACKS.map(track => (
              <TrackCard 
                key={track.id} 
                track={track} 
                onSelect={(level) => {
                  setActiveFilter(level.charAt(0).toUpperCase() + level.slice(1));
                  document.getElementById('courses')?.scrollIntoView({ behavior: 'smooth' });
                }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Featured Courses */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">Featured Courses</h2>
              <p className="text-blue-100/60">Start with our most popular courses</p>
            </div>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredCourses.map(course => (
              <CourseCard key={course.id} course={course} onClick={setSelectedCourse} />
            ))}
          </div>
        </div>
      </section>

      {/* Why FonderX Academy */}
      <section className="py-16 px-4 bg-[#001020]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">Why FonderX Academy?</h2>
            <p className="text-blue-100/60 max-w-2xl mx-auto">Everything you need to understand cryptocurrency, all in one place.</p>
          </div>
          <WhyFonderXAcademy />
        </div>
      </section>

      {/* All Courses */}
      <section id="courses" className="py-16 px-4 scroll-mt-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">All Courses</h2>
              <p className="text-blue-100/60">{filteredCourses.length} courses available</p>
            </div>
            
            <div className="flex items-center gap-3">
              <Filter className="w-5 h-5 text-blue-100/50" />
              <FilterButtons activeFilter={activeFilter} onFilterChange={setActiveFilter} />
            </div>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCourses.map(course => (
              <CourseCard key={course.id} course={course} onClick={setSelectedCourse} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-[#001020] to-[#002050]">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#3B82F6]/20 text-[#3B82F6] px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Globe className="w-4 h-4" />
            Ready to Get Started?
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">Start Your Crypto Journey Today</h2>
          <p className="text-blue-100/60 text-lg mb-8 max-w-2xl mx-auto">
            Join thousands of learners mastering cryptocurrency. All courses are free — start learning now.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => {
                setSelectedCourse(COURSES[0]);
              }}
              className="inline-flex items-center justify-center gap-2 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold px-8 py-4 rounded-xl transition-all shadow-lg shadow-[#3B82F6]/25"
              data-testid="start-learning-cta"
            >
              <Play className="w-5 h-5" />
              Start First Course
            </button>
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-8 py-4 rounded-xl transition-all">
              Contact Us
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 px-4 bg-[#001020] border-t border-white/5">
        <div className="max-w-4xl mx-auto">
          <p className="text-blue-100/40 text-xs text-center leading-relaxed">
            <strong className="text-blue-100/60">Disclaimer:</strong> The information provided in FonderX Academy is for educational purposes only and should not be considered financial, investment, or legal advice. Cryptocurrency investments are highly speculative and involve substantial risk of loss. Always conduct your own research.
          </p>
        </div>
      </section>

      {/* Course Modal */}
      {selectedCourse && (
        <CourseModal 
          course={selectedCourse} 
          onClose={() => setSelectedCourse(null)}
          onStartCourse={handleStartCourse}
        />
      )}
    </div>
  );
}
